import { Alert, Button, Stack } from '@mui/material';
import type { FC } from 'react';
import React, { useCallback, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { spAppMainPages } from '@pflegenavi/frontend/routing';
import { useGetServiceProviderPhoenix } from '@pflegenavi/service-provider-frontend/api';
import { useAuthentication } from '@pflegenavi/frontend/authentication';
import { useBankAccountStatus } from '../../hooks/useBankAccountStatus';

const DISMISS_KEY_PREFIX = 'dismiss-bank-account-banner-';

export const BankAccountSetupBanner: FC = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { user } = useAuthentication();
  const userId = user?.userId;

  const { data: serviceProvider } = useGetServiceProviderPhoenix();
  const { isReady, recipientPending, scaUrl, openScaUrl } =
    useBankAccountStatus();

  const dismissKey = userId ? `${DISMISS_KEY_PREFIX}${userId}` : null;
  const [isDismissed, setIsDismissed] = useState(() => {
    if (!dismissKey) {
      return false;
    }
    return localStorage.getItem(dismissKey) === 'true';
  });

  const handleDismiss = useCallback(() => {
    if (dismissKey) {
      localStorage.setItem(dismissKey, 'true');
      setIsDismissed(true);
    }
  }, [dismissKey]);

  const handleSetupBankAccount = useCallback(() => {
    navigate(spAppMainPages.SETUP_BANK_ACCOUNT);
  }, [navigate]);

  // Show the banner when the user is on MangoPay and the bank account is not usable:
  // never set up, PENDING (form submitted but SCA not completed — e.g. the user closed
  // the browser, or completed the form on mobile and came back to web), or
  // CANCELED/DEACTIVATED (needs to set up a new one).
  const showBanner =
    !isDismissed &&
    serviceProvider?.data?.payment_service_provider === 'mangopay' &&
    serviceProvider?.data?.mangopay_user_id &&
    !isReady;

  if (!showBanner) {
    return null;
  }

  return (
    <Alert
      severity="warning"
      onClose={handleDismiss}
      sx={{ mb: 3 }}
      action={
        <Stack
          direction="row"
          spacing={1}
          sx={{
            alignItems: 'center',
          }}
        >
          {recipientPending && scaUrl ? (
            <Button
              color="inherit"
              size="small"
              variant="outlined"
              onClick={openScaUrl}
            >
              {t(
                'service-provider-app.dashboard.resume-bank-account-verification-button'
              )}
            </Button>
          ) : (
            <Button
              color="inherit"
              size="small"
              variant="outlined"
              onClick={handleSetupBankAccount}
            >
              {t('service-provider-app.dashboard.setup-bank-account-button')}
            </Button>
          )}
        </Stack>
      }
    >
      {recipientPending
        ? t('service-provider-app.dashboard.bank-account-verification-pending')
        : t('service-provider-app.dashboard.bank-account-setup-required')}
    </Alert>
  );
};
